"use client"

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import type { StepProps } from "@/types/listing"
import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { motion } from "framer-motion"
import { ChevronRight, Calendar as CalendarIcon, Code2, Clock } from "lucide-react"
import { format, addDays, setHours, setMinutes, startOfDay } from "date-fns"
import { getDateFnsLocale } from "@/lib/i18n/locale-utils"
import { createClient } from "@/utils/supabase/client"
import { useToast } from "@/hooks/use-toast"
import { creationFlowBlockClass, creationFlowSectionLabelClass, creationFlowMutedClass } from "@/lib/creation-flow-form-styles"
import { cn } from "@/lib/utils"
import { useLocale, useTranslations } from "next-intl"

export function ScheduleStep({
  data,
  updateDataAction,
  onNextAction,
  hideButton,
  onValidationChange,
  onRegisterSave,
  compact,
}: StepProps) {
  const locale = useLocale()
  const tLC = useTranslations("listingCreation")
  const { toast } = useToast()
  
  const [publishNow, setPublishNow] = useState<boolean>(
    !data.scheduled_publish_at
  )

  // Dev listing state
  const isDevEnvironment = process.env.NODE_ENV === 'development'

  const [isDevListing, setIsDevListing] = useState<boolean>(
    data.is_dev_listing ?? false
  )

  // Check if user is in dev listing allowlist
  const [canViewDev, setCanViewDev] = useState<boolean>(false)
  const [checkingAllowlist, setCheckingAllowlist] = useState<boolean>(true)

  useEffect(() => {
    const checkAllowlist = async () => {
      try {
        const response = await fetch('/api/dev-listing-allowlist?checkCurrentUser=true')

        if (response.ok) {
          const data = await response.json()
          setCanViewDev(Boolean(data.canViewDev))
        } else {
          setCanViewDev(false)
        }
      } catch (error) {
        console.error('Error checking dev listing access:', error)
        setCanViewDev(false)
      } finally {
        setCheckingAllowlist(false)
      }
    }

    checkAllowlist()
  }, [])

  // Date/Time state (local timezone)
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    data.scheduled_publish_at 
      ? new Date(data.scheduled_publish_at)
      : null
  )
  const [selectedTime, setSelectedTime] = useState<string>(
    data.scheduled_publish_at
      ? format(new Date(data.scheduled_publish_at), 'HH:mm')
      : '18:00'
  )
  
  // For native date input (mobile)
  const [nativeDateValue, setNativeDateValue] = useState<string>(
    data.scheduled_publish_at
      ? format(new Date(data.scheduled_publish_at), 'yyyy-MM-dd')
      : ''
  )
  
  // Calendar popover state (desktop only)
  const [calendarOpen, setCalendarOpen] = useState<boolean>(false)
  
  // Validation state for date/time
  const [isDateTimeValid, setIsDateTimeValid] = useState<boolean>(true)
  const [validationError, setValidationError] = useState<string>('')
  
  // Refs for native inputs (mobile)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)
  
  // Refs to track if changes are from user interaction
  const isUserChangingPublishNow = useRef(false)
  const isUserChangingDevListing = useRef(false)

  // Get user's timezone
  const userTimezone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  }, [])

  const dateLocale = getDateFnsLocale(locale)

  // Generate quick date options
  const quickDates = useMemo(() => {
    const now = new Date()
    return [
      { label: tLC("today"), date: now },
      { label: tLC("tomorrow"), date: addDays(now, 1) },
      { label: format(addDays(now, 2), 'EEE, MMM d', { locale: dateLocale }), date: addDays(now, 2) },
      { label: format(addDays(now, 3), 'EEE, MMM d', { locale: dateLocale }), date: addDays(now, 3) },
      { label: format(addDays(now, 7), 'EEE, MMM d', { locale: dateLocale }), date: addDays(now, 7) },
    ]
  }, [dateLocale, tLC])

  // Validate
  useEffect(() => {
    const isValid = publishNow || (selectedDate !== null && isDateTimeValid)
    onValidationChange?.(isValid)
  }, [publishNow, selectedDate, isDateTimeValid, onValidationChange])

  // Sync publishNow with scheduled_publish_at ONLY
  useEffect(() => {
    // Only update publishNow if it's not a user interaction
    if (!isUserChangingPublishNow.current) {
      const newPublishNow = !data.scheduled_publish_at
      if (newPublishNow !== publishNow) {
        setPublishNow(newPublishNow)
      }
    }
    
    if (data.scheduled_publish_at) {
      const date = new Date(data.scheduled_publish_at)
      setSelectedDate(date)
      setSelectedTime(format(date, 'HH:mm'))
      setNativeDateValue(format(date, 'yyyy-MM-dd'))
    }
  }, [data.scheduled_publish_at, publishNow])

  // Sync dev listing state
  useEffect(() => {
    if (!isUserChangingDevListing.current) {
      if (data.is_dev_listing !== undefined) {
        setIsDevListing(data.is_dev_listing)
      }
    }
  }, [data.is_dev_listing])

  const handlePublishNowChange = (checked: boolean) => {
    // Mark as user interaction to prevent sync effect from overriding
    isUserChangingPublishNow.current = true
    setPublishNow(checked)
    
    if (checked) {
      setSelectedDate(null)
      setNativeDateValue('')
      updateDataAction({ scheduled_publish_at: null })
    } else {
      // Preselect today's date when scheduling is enabled
      const todayStart = startOfDay(new Date())
      setSelectedDate(todayStart)
      setNativeDateValue(format(todayStart, 'yyyy-MM-dd'))
      // Validate immediately with the current selected time
      validateAndUpdateDateTime(todayStart, selectedTime)
    }
    
    // Reset the flag after a short delay
    setTimeout(() => {
      isUserChangingPublishNow.current = false
    }, 100)
  }

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    setNativeDateValue(format(date, 'yyyy-MM-dd'))
    updateScheduledDateTime(date, selectedTime)
  }
  
  const handleNativeDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    
    if (value) {
      const date = new Date(value)
      if (!isNaN(date.getTime())) {
        const dateAtStart = startOfDay(date)
        setNativeDateValue(value)
        setSelectedDate(dateAtStart)
        
        // Validate with time
        validateAndUpdateDateTime(dateAtStart, selectedTime)
      }
    } else {
      setNativeDateValue(value)
    }
  }

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value
    setSelectedTime(time)
    
    if (selectedDate) {
      // Validate with date
      validateAndUpdateDateTime(selectedDate, time)
    }
  }
  
  const validateAndUpdateDateTime = (date: Date, time: string) => {
    const [hours, minutes] = time.split(':').map(Number)
    let scheduledDateTime = new Date(date)
    scheduledDateTime = setHours(scheduledDateTime, hours)
    scheduledDateTime = setMinutes(scheduledDateTime, minutes)
    
    const now = new Date()
    
    // Check if scheduled time is in the past
    if (scheduledDateTime <= now) {
      setIsDateTimeValid(false)
      setValidationError(tLC("scheduleValidationFuture"))
      return
    }
    
    // Check maximum limit: 90 days from now
    const maxDate = addDays(now, 90)
    if (scheduledDateTime > maxDate) {
      setIsDateTimeValid(false)
      setValidationError(tLC("scheduleValidationMaxDays"))
      return
    }
    
    // Valid - update
    setIsDateTimeValid(true)
    setValidationError('')
    updateDataAction({
      scheduled_publish_at: scheduledDateTime.toISOString()
    })
  }

  const updateScheduledDateTime = (date: Date, time: string) => {
    // Use the validation function
    validateAndUpdateDateTime(date, time)
  }

  const handleDevListingChange = (checked: boolean) => {
    isUserChangingDevListing.current = true
    setIsDevListing(checked)
    updateDataAction({ is_dev_listing: checked })

    setTimeout(() => {
      isUserChangingDevListing.current = false
    }, 100)
  }

  const flushScheduleToData = useCallback(() => {
    let scheduledAt: string | null = null
    if (!publishNow && selectedDate) {
      const [hours, minutes] = selectedTime.split(':').map(Number)
      let dt = new Date(selectedDate)
      dt = setHours(dt, hours)
      dt = setMinutes(dt, minutes)
      scheduledAt = dt.toISOString()
    }
    updateDataAction({
      scheduled_publish_at: scheduledAt,
      is_dev_listing: (canViewDev) ? isDevListing : false,
    })
  }, [publishNow, selectedDate, selectedTime, isDevListing, canViewDev, updateDataAction])

  const handleNext = () => {
    flushScheduleToData()
    onNextAction()
  }

  useEffect(() => {
    onRegisterSave?.(flushScheduleToData)
    return () => onRegisterSave?.(null)
  }, [onRegisterSave, flushScheduleToData])

  // Format display
  const formattedScheduledTime = useMemo(() => {
    if (!selectedDate) return null
    
    const [hours, minutes] = selectedTime.split(':').map(Number)
    let dt = new Date(selectedDate)
    dt = setHours(dt, hours)
    dt = setMinutes(dt, minutes)
    
    return {
      date: format(dt, 'EEEE, MMMM d, yyyy', { locale: dateLocale }),
      time: format(dt, 'p', { locale: dateLocale }),
      timezone: userTimezone
    }
  }, [dateLocale, selectedDate, selectedTime, userTimezone])

  // Min date for native input
  const minDate = format(new Date(), 'yyyy-MM-dd')
  
  // Max date for native input (90 days from now)
  const maxDate = useMemo(() => {
    return format(addDays(new Date(), 90), 'yyyy-MM-dd')
  }, [])
  
  // Min time for native input (current time if today, or 00:00 if future date)
  const minTime = useMemo(() => {
    if (!selectedDate) return '00:00'
    
    const today = new Date()
    const isToday = format(selectedDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
    
    if (isToday) {
      // Add 1 minute to current time to ensure it's in the future
      const now = new Date()
      const nextMinute = new Date(now.getTime() + 60000)
      return format(nextMinute, 'HH:mm')
    }
    
    return '00:00'
  }, [selectedDate])

  const buttonClasses = compact ? "w-full h-14 text-sm shadow-md rounded-full group" : "w-full h-16 text-base shadow-lg rounded-full group"
  const cardPadding = compact ? "p-3" : "p-4"
  const cardRadius = "rounded-3xl"
  const cardBase = creationFlowBlockClass
  const sectionLabel = creationFlowSectionLabelClass
  const mutedClass = creationFlowMutedClass
  /*
    text-base (16px) prevents iOS Safari from auto-zooming into the
    native date/time picker on focus.
  */
  const inputBase =
    "px-3 py-2.5 text-base rounded-xl bg-muted text-foreground focus:outline-none focus:ring-2 focus:ring-muted-foreground/70 focus:ring-offset-2 focus:ring-offset-sheet"

  return (
    <div className={compact ? "space-y-4 px-1 pt-2" : "space-y-6 px-2 pt-4"}>
      <motion.div
        className={compact ? "space-y-4" : "space-y-5"}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className={cn(cardRadius, cardBase, cardPadding)}>
          <p className={cn(sectionLabel, "mb-3")}>{tLC("schedulePublicationTitle")}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalendarIcon className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label htmlFor="publish-now" className="text-sm font-medium cursor-pointer text-foreground">
                  {tLC("publishImmediately")}
                </Label>
                <p className={cn("text-xs mt-0.5", mutedClass)}>
                  {tLC("publishImmediatelyDesc")}
                </p>
              </div>
            </div>
            <div>
              <Switch
                id="publish-now"
                checked={publishNow}
                onCheckedChange={handlePublishNowChange}
              />
            </div>
          </div>
        </div>

        {!publishNow && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={cn(cardRadius, cardBase, cardPadding, "space-y-4")}
          >
            <div>
              <p className={cn(sectionLabel, "mb-1")}>{tLC("schedulePublicationLabel")}</p>
              <p className={cn("text-xs mb-3", mutedClass)}>
                {tLC("schedulePublicationDesc")}
              </p>
            </div>

            <div className="space-y-2">
              <Label className={cn("text-xs", mutedClass)}>{tLC("quickSelect")}</Label>
              <div className="grid grid-cols-2 gap-2">
                {quickDates.map((quick) => {
                  const isSelected = selectedDate && format(selectedDate, 'yyyy-MM-dd') === format(quick.date, 'yyyy-MM-dd')
                  return (
                    <Button
                      key={quick.label}
                      type="button"
                      variant={isSelected ? 'default' : 'outline-solid'}
                      onClick={() => handleDateSelect(quick.date)}
                      className={cn(
                        "justify-start text-sm",
                        "border-0",
                        !isSelected && "bg-muted text-foreground hover:bg-sheet-handle",
                        isSelected && "bg-primary text-primary-foreground hover:bg-primary/90 ring-2 ring-primary ring-offset-2 ring-offset-sheet"
                      )}
                      size="sm"
                    >
                      {quick.label}
                    </Button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label className={cn("text-xs", mutedClass)}>{tLC("orChooseDateTime")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  ref={dateInputRef}
                  type="date"
                  value={nativeDateValue}
                  onChange={handleNativeDateChange}
                  min={minDate}
                  max={maxDate}
                  className={inputBase}
                />
                <input
                  ref={timeInputRef}
                  type="time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  min={minTime}
                  className={inputBase}
                />
              </div>
              {!isDateTimeValid && validationError && (
                <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-xl p-2">
                  {validationError}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {!checkingAllowlist && canViewDev && (
          <div className={cn(cardRadius, cardBase, cardPadding)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Code2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="dev-listing" className="text-sm font-medium cursor-pointer text-foreground">
                    {tLC("developmentListing")}
                    {isDevEnvironment && (
                      <span className={cn("ml-2 text-xs font-normal", mutedClass)}>
                        ({tLC("autoEnabledInDev")})
                      </span>
                    )}
                  </Label>
                  <p className={cn("text-xs mt-0.5", mutedClass)}>
                    {tLC("developmentListingDesc")}
                  </p>
                </div>
              </div>
              <div>
                <Switch
                  id="dev-listing"
                  checked={isDevListing}
                  onCheckedChange={handleDevListingChange}
                />
              </div>
            </div>
            {isDevListing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-3 rounded-xl bg-orange-500/10 border border-orange-500/20 p-3"
              >
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  <span className="font-semibold">{tLC("devModePrefix")}</span> {tLC("devModeWarning")}
                  {isDevEnvironment && (
                    <span className="block mt-1">
                      {tLC("devModeAutoEnabled")}
                    </span>
                  )}
                </p>
              </motion.div>
            )}
          </div>
        )}

        <div className="rounded-2xl p-3 bg-muted/40">
          <p className={cn("text-xs", mutedClass)}>
            {tLC("scheduleHintPrefix")} {(data as { listing_format?: string }).listing_format === "auction"
              ? tLC("auctionScheduleHint")
              : publishNow
                ? tLC("publishImmediatelyHint")
                : selectedDate && formattedScheduledTime
                  ? tLC("publishScheduledHint", {
                      date: formattedScheduledTime.date,
                      time: formattedScheduledTime.time,
                      timezone: formattedScheduledTime.timezone,
                    })
                  : tLC("selectDateTimeHint")}
          </p>
        </div>
      </motion.div>

      {!hideButton && (
        <div className="pt-6">
          <Button
            onClick={handleNext}
            disabled={!publishNow && (!selectedDate || !isDateTimeValid)}
            className={buttonClasses}
            size={compact ? "default" : "lg"}
          >
            {tLC("continue")}
            <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      )}
    </div>
  )
}
